import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import format from 'date-fns/format';
import _get from 'lodash/get';
import uniq from 'lodash/uniq';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import FluidImg from 'Components/Utility/FluidImg';
import HitCardLabel from 'Components/Cards/HitCardLabel';
import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import HitCardGiftBtn from 'Components/Cards/HitCardGiftBtn';
import HitCardTieredGiftBtn from 'Components/Cards/HitCardTieredGiftBtn';
import Drm from 'Components/Utility/Drm';
import HitCardWishlistBtn from 'Components/Cards/HitCardWishlistBtn';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import useViewport from 'Hooks/useViewport';
import Price from 'Containers/Utility/Price/Price';

import 'Components/Wishlist/WishlistCard.scss';

const messages = defineMessages({
  add_to_cart: {
    id: 'WishlistCard.add_to_cart',
    defaultMessage: 'Add To Cart',
  },
});

const WishlistCard = ({
  displayName,
  giftButton,
  wishlistId,
  hit,
  inLibrary,
  inSteamLibrary,
  authenticated,
  allPickAndMixes,
  cartSlugs,
}) => {
  const { formatMessage } = useIntl();
  const { isBelowMd } = useViewport();

  const {
    name,
    type,
    slug,
    cover,
    product_id: productId,
    best_ever: bestEver,
    release_date: releaseDate,
    available_valid_until: endingDate,
    hideDiscount,
    presale,
    free_to_play: freeToPlay,
    giveaway,
    drm,
    discount_percent: discountPercent,
    price,
    tiered,
    link,
    operating_systems: OS,
    wishlistDate,
    fullPrice,
    pay_what_you_want: payWhatYouWant,
    bundle_tiers: bundleTiers,
    display_type: displayType,
    star_deal: isStarDeal,
  } = hit;

  const getPickAndMixByProductSlug = () => {
    let matchingPickAndMix;

    allPickAndMixes.forEach((pnm) => {
      const pickAndMixProducts = pnm.products;
      const hasMatch = pickAndMixProducts.find(product => product.slug === slug);

      if (hasMatch) matchingPickAndMix = pnm;
    });

    return matchingPickAndMix;
  };

  const pickAndMix = getPickAndMixByProductSlug(slug, allPickAndMixes);
  const isInPickAndMix = !!pickAndMix;

  const getUpsellData = () => {
    if (!isInPickAndMix) return false;

    const productSlugs = uniq(_get(pickAndMix, 'products', []).map(pnmProduct => pnmProduct.slug));
    const pnmTiers = _get(pickAndMix, 'tiers', []);
    const productSlugsInCart = productSlugs.filter(productSlug => cartSlugs.includes(productSlug));
    const pnmProductsInCartCount = _get(productSlugsInCart, 'length', 0);
    const upsellTier = pnmTiers.find(tier => tier.quantity > pnmProductsInCartCount);

    if (!upsellTier) return false;

    const toAdd = upsellTier.quantity - pnmProductsInCartCount;
    return { toAdd, quantity: upsellTier.quantity, price: upsellTier.price };
  };

  const upsellData = getUpsellData();

  const showPNMLabel = isInPickAndMix && upsellData && upsellData.toAdd > 0;

  const DrmOSIcons = () =>
    <div className="icons-container">
      <Drm drmType={drm[0]} />
      <div className="card-os-icons">
        {OS.map(os =>
          <span key={os}>
            {os === 'windows' &&
              <FontAwesomeIcon icon={fabFaWindows} className="mr-2" />
            }
            {os === 'mac' &&
              <FontAwesomeIcon icon={fabFaApple} className="mr-2" />
            }
            {os === 'linux' &&
              <FontAwesomeIcon icon={fabFaLinux} className="mr-2" />
            }
          </span>,
        )}
      </div>
    </div>;

  const PnmLabel = () => {
    if (!upsellData) return false;
    return (
      <div className="wishlist-pnm-label">
        <div className="pnm-label-title">{pickAndMix.name}</div>
        <div>
          <Link to={`/pick-and-mix/${pickAndMix.slug}`} className="pnm-label-upsell">

            <FormattedMessage
              id="wishlist.card.pnmupsellmessageproducts"
              defaultMessage="Add {productsToAdd, plural, one {# more product} other {# more products}} to qualify for {tier} for {price}"
              values={{
                productsToAdd: upsellData.toAdd,
                tier: upsellData.quantity,
                price: <>&nbsp;<Price price={upsellData.price} /></>,
              }}
            />
            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </Link>
        </div>
      </div>
    );
  };

  if (isBelowMd) {
    return (
      <React.Fragment>
        {showPNMLabel &&
          <PnmLabel pickAndMix={pickAndMix} />
        }

        <div
          className={classNames(
            'faux-block-link wishlist-card',
            { 'wishlist-card__in-library': inLibrary },
            { 'wishlist-card__in-steam-library': inSteamLibrary },
            { 'no-top-border-radius': showPNMLabel },
          )}
        >
          <div className="image-title-container">
            <div className="image-container">
              <div className="responsive-image responsive-image--16by9">
                <FluidImg
                  src={cover}
                  alt={name}
                />
              </div>
            </div>

            <div className="name-icons">
              {!link &&
                <Link className="faux-block-link__overlay-link" to={`/${type}/${slug}`}>
                  <div className="item-name">{name}</div>
                </Link>
              }
              {link &&
                <Link className="faux-block-link__overlay-link" to={link}>
                  <div className="item-name">{name}</div>
                </Link>
              }
              {wishlistDate &&
                <div className="wishlist-info">
                  <p>
                    <FormattedMessage
                      id="wishlist.card.added"
                      defaultMessage="Added {dateAdded}"
                      values={{ dateAdded: format(wishlistDate, 'DD/MM/YYYY') }}
                    />
                  </p>
                </div>
              }
            </div>
          </div>

          <div className="drm-price-container">
            <DrmOSIcons />

            <div className="pricing">
              {giveaway &&
                <div><FormattedMessage id="wishlist.card.free" defaultMessage="FREE" /></div>
              }
              {(presale && !freeToPlay) &&
                <div><FormattedMessage id="wishlist.card.availablesoon" defaultMessage="Available Soon" /></div>
              }
              {!presale && !giveaway && !freeToPlay &&
                <PriceContainer
                  discountPercent={discountPercent}
                  price={price}
                  tiered={tiered}
                  hideDiscount={hideDiscount}
                  slug={slug}
                  fullPrice={fullPrice}
                  payWhatYouWant={payWhatYouWant}
                />
              }
            </div>
          </div>

          <div className="buttons-container">
            <HitCardWishlistBtn
              productId={hit.product_id}
              slug={hit.slug} hit={hit}
              componentType="WishlistCard"
            />

            {!hideDiscount && !tiered && !presale && !payWhatYouWant && !giftButton &&
              <AddToCartBtn
                showInCartLink
                parentComponent="WishlistCard"
                id={productId}
                slug={slug}
                buttonText={formatMessage(messages.add_to_cart)}
              />
            }

            {giftButton && !tiered &&
              <HitCardGiftBtn displayName={displayName} hit={hit} wishlistId={wishlistId} />
            }
            {giftButton && tiered &&
              <HitCardTieredGiftBtn
                displayName={displayName}
                hit={hit}
                wishlistId={wishlistId}
                bundleTiers={bundleTiers}
              />
            }
          </div>
        </div>
      </React.Fragment>
    );
  }

  let labelOverride;

  if (inLibrary && authenticated && !hit.mystery) {
    labelOverride = 'inLibrary';
  } else if (inSteamLibrary && authenticated && !hit.mystery) {
    labelOverride = 'inSteamLibrary';
  }

  return (
    <React.Fragment>
      {showPNMLabel &&
        <PnmLabel pickAndMix={pickAndMix} />
      }
      <div
        className={classNames(
          'faux-block-link wishlist-card',
          { 'wishlist-card__in-library': inLibrary },
          { 'wishlist-card__in-steam-library': inSteamLibrary },
          { 'no-top-border-radius': showPNMLabel },
        )}
      >
        <div className="image-container">
          {!showPNMLabel &&
            <HitCardLabel
              slug={slug}
              bestDiscount={bestEver}
              preorderDate={releaseDate}
              endingDate={endingDate}
              type={type}
              displayType={displayType}
              presale={presale}
              isStarDeal={isStarDeal}
              labelOverride={labelOverride}
            />
          }
          <div className="responsive-image responsive-image--16by9">
            <FluidImg
              src={cover}
              alt={name}
            />
          </div>
        </div>
        <div className="content-container">
          <div className="name-icons">
            {!link &&
              <Link className="faux-block-link__overlay-link" to={`/${type}/${slug}`}>
                <div className="item-name">{name}</div>
              </Link>
            }
            {link &&
              <Link className="faux-block-link__overlay-link" to={link}>
                <div className="item-name">{name}</div>
              </Link>
            }
            {wishlistDate &&
              <div className="wishlist-info">
                <FormattedMessage
                  id="wishlist.card.added"
                  defaultMessage="Added {dateAdded}"
                  values={{ dateAdded: format(wishlistDate, 'DD/MM/YYYY') }}
                />
              </div>
            }

            <DrmOSIcons />

          </div>
          <div className="price-container">
            <div className="pricing">
              {giveaway &&
                <div><FormattedMessage id="wishlist.card.free" defaultMessage="FREE" /></div>
              }
              {(presale && !freeToPlay) &&
                <div><FormattedMessage id="wishlist.card.availablesoon" defaultMessage="Available Soon" /></div>
              }
              {!presale && !giveaway && !freeToPlay &&
                <PriceContainer
                  discountPercent={discountPercent}
                  price={price}
                  tiered={tiered}
                  hideDiscount={hideDiscount}
                  slug={slug}
                  fullPrice={fullPrice}
                  payWhatYouWant={payWhatYouWant}
                />
              }
            </div>
            <div className="buttons-container">
              <HitCardWishlistBtn
                productId={hit.product_id}
                slug={hit.slug} hit={hit}
                componentType="WishlistCard"
              />

              {!hideDiscount && !tiered && !presale && !payWhatYouWant && !giftButton &&
                <AddToCartBtn
                  showInCartLink
                  parentComponent="WishlistCard"
                  id={productId}
                  slug={slug}
                  buttonText={formatMessage(messages.add_to_cart)}
                />
              }

              {giftButton && !tiered &&
                <HitCardGiftBtn displayName={displayName} hit={hit} wishlistId={wishlistId} />
              }
              {giftButton && tiered &&
                <HitCardTieredGiftBtn
                  displayName={displayName}
                  hit={hit}
                  wishlistId={wishlistId}
                  bundleTiers={bundleTiers}
                />
              }
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

WishlistCard.propTypes = {
  hit: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    display_type: PropTypes.string,
    slug: PropTypes.string,
    cover: PropTypes.string,
    product_id: PropTypes.string,
    hideDiscount: PropTypes.bool,
    best_ever: PropTypes.bool,
    release_date: PropTypes.number,
    available_valid_until: PropTypes.number,
    presale: PropTypes.bool,
    freeToPlay: PropTypes.bool,
    giveaway: PropTypes.bool,
    drm: PropTypes.array,
    operating_systems: PropTypes.array,
    discount_percent: PropTypes.number,
    price: PropTypes.shape({}),
    fullPrice: PropTypes.shape({}),
    tiered: PropTypes.bool,
    wishlistDate: PropTypes.string,
    link: PropTypes.string,
    free_to_play: PropTypes.bool,
  }),
  inLibrary: PropTypes.bool,
  inSteamLibrary: PropTypes.bool,
  giftButton: PropTypes.bool,
  displayName: PropTypes.string,
  wishlistId: PropTypes.string,
  authenticated: PropTypes.bool,
  allPickAndMixes: PropTypes.arrayOf(PropTypes.object),
};

WishlistCard.defaultProps = {
  hit: {
    name: '',
    type: 'game',
    slug: '',
    cover: '',
    product_id: '',
    hideDiscount: false,
    best_ever: false,
    release_date: 0,
    available_valid_until: 0,
    freeToPlay: false,
    presale: false,
    giveaway: false,
    drm: [],
    discount_percent: 0,
    price: {},
    fullPrice: {},
    tiered: false,
    operating_systems: [],
    wishlistDate: '',
    link: '',
    free_to_play: false,
  },
  inLibrary: false,
  inSteamLibrary: false,
  giftButton: false,
  displayName: '',
  wishlistId: '',
  authenticated: false,
  allPickAndMixes: [],
};

const mapStateToProps = ({
  userLibrary,
  steamLibrary,
  auth: { authenticated },
  pickAndMix: { all: allPickAndMixes },
  cart: { allSlugs: cartSlugs },
}, { hit: { name } }) => ({
  inLibrary: userLibrary.includes(name),
  inSteamLibrary: steamLibrary.includes(name),
  allPickAndMixes,
  authenticated,
  cartSlugs,
});


export default connect(mapStateToProps)(WishlistCard);
